import React, { useEffect, useState } from "react";
import { MoreHorizontal, ThumbsUp, ThumbsDown, XCircle, HelpCircle } from "lucide-react";
import api from "@/lib/api";

// Renders the winning ad for a placement (server-side selection) with a
// non-intrusive one-tap feedback affordance + "Why am I seeing this?" (spec §3, §6).
export const SponsorAd = ({ placement = "calendar", className = "", ambient = false, maxVh = 46, adData, fallback = null, teaser = false, noClick = false }) => {
  const [ad, setAd] = useState(adData || null);
  const [menu, setMenu] = useState(false);
  const [why, setWhy] = useState(false);
  const [gone, setGone] = useState(false);

  const fetchAd = () => api.get(`/ads/serve?placement=${placement}`)
    .then((r) => setAd(r.data)).catch(() => {});
  useEffect(() => { if (adData === undefined) fetchAd(); else setAd(adData); }, [placement, adData]);

  const feedback = (type) => {
    if (ad?.ad?.campaign_id) {
      api.post("/ads/feedback", { campaign_id: ad.ad.campaign_id, feedback_type: type }).catch(() => {});
    }
    setMenu(false);
    if (type !== "more") setGone(true);   // hide immediately for less/not-interested
    else fetchAd();
  };

  const trackClick = () => {
    if (ad?.ad?.campaign_id) {
      api.post("/ads/click", { campaign_id: ad.ad.campaign_id, placement, variant_id: ad.ad.variant_id }).catch(() => {});
    }
  };

  if (gone || !ad || !ad.ad) return fallback;
  const isFeature = placement === "countdown_feature";
  if (teaser) return (
    <div className={`w-full max-w-[220px] mx-auto rounded-xl overflow-hidden border border-race-line pointer-events-none ${className}`} data-testid="sponsor-ad-feature-teaser">
      {ad.ad.image_url && <img src={ad.ad.image_url} alt={ad.ad.name} className="w-full block object-cover object-bottom" style={{ aspectRatio: "4 / 5" }} />}
    </div>
  );
  return (
    <div className={`relative ${isFeature ? "w-full max-w-[460px] mx-auto" : "bg-race-surface border border-race-line rounded-lg overflow-hidden w-full max-w-[480px] mx-auto"} ${className}`} data-testid={`sponsor-ad-${placement}`}>
      {ad.ad.image_url ? (
        <a href={noClick ? undefined : ad.ad.click_url || "#"} target={noClick ? undefined : "_blank"} rel="noreferrer"
          data-testid={noClick ? "sponsor-ad-display-only" : "sponsor-ad-click"}
          className={`block ${noClick ? "pointer-events-none select-none" : ""}`} onClick={noClick ? undefined : trackClick} onAuxClick={noClick ? undefined : trackClick}>
          <img src={ad.ad.image_url} alt={ad.ad.name}
            className={`w-full block ${isFeature ? "h-auto object-contain rounded-xl mx-auto" : "h-auto object-cover"}`}
            style={isFeature
              ? { aspectRatio: `${ad.ad.width || 1080} / ${ad.ad.height || 1350}`, maxHeight: `${maxVh}vh`, width: "auto", maxWidth: "100%", margin: "0 auto" }
              : { aspectRatio: `${ad.ad.width || 324} / ${ad.ad.height || 132}` }} />
        </a>
      ) : (
        <div className="px-4 py-5 text-center">
          <div className="font-heading text-lg text-race-txt">{ad.ad.name}</div>
          <div className="text-xs text-race-sub">{ad.ad.sponsor}</div>
        </div>
      )}
      <button onClick={() => setMenu((m) => !m)} data-testid="sponsor-ad-feedback-btn"
        className={`absolute top-2 right-2 w-6 h-6 grid place-items-center rounded-full bg-black/50 text-white/80 hover:bg-black/70 ${ambient ? "hidden" : ""}`}>
        <MoreHorizontal size={14} />
      </button>
      {menu && !ambient && (
        <div className="absolute top-9 right-2 z-10 bg-race-bg border border-race-line rounded-md shadow-xl text-sm py-1 w-44" data-testid="sponsor-ad-menu">
          <button onClick={() => feedback("more")} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-race-surface text-race-txt"><ThumbsUp size={13} /> More like this</button>
          <button onClick={() => feedback("less")} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-race-surface text-race-txt"><ThumbsDown size={13} /> Less like this</button>
          <button onClick={() => feedback("not_interested")} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-race-surface text-race-sub"><XCircle size={13} /> Not interested</button>
          <button onClick={() => { setWhy((w) => !w); setMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-race-surface text-race-sub border-t border-race-line"><HelpCircle size={13} /> Why am I seeing this?</button>
        </div>
      )}
      {why && (
        <div className="px-3 py-2 text-xs text-race-sub bg-black/40" data-testid="sponsor-ad-why">{ad.why}</div>
      )}
    </div>
  );
};

export default SponsorAd;
